import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  PlusCircle,
  X,
  Building2,
  FileText,
  Loader2,
  TrendingUp,
  Users,
  DollarSign,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Heart,
  Infinity,
  UserMinus,
  Tag,
  CalendarDays,
} from "lucide-react";
import CompanyTable from "../../components/internal/CompanyTable";
import DashboardFilters from "../../components/internal/DashboardFilters";
import TrendChart, {
  generateTrendData,
} from "../../components/internal/TrendChart";
import CFOAssistant from "../../components/internal/CFOAssistant";
import BillingAuditPanel from "../../components/internal/BillingAuditPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiFetch } from "@/lib/api";

const BILLING_BATCH_SIZE = 50;

export default function Dashboard() {
  const { companies: layoutCompanies, refreshCompanies } =
    useOutletContext() || {};
  const [billingData, setBillingData] = useState({});
  const [isLoading, setIsLoading] = useState(!layoutCompanies?.length);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null); // null = create, object = edit
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [availablePlans, setAvailablePlans] = useState([]);
  const [formData, setFormData] = useState({
    companyName: "",
    cnpj: "",
    planType: "",
  });

  const [filters, setFilters] = useState({
    period: "all",
    status: "all",
    plan: "all",
  });

  const allCompanies = layoutCompanies || [];

  // Apply filters
  const companies = useMemo(() => {
    let filtered = allCompanies;

    // Period filter
    if (filters.period !== "all") {
      const now = new Date();
      const monthsBack =
        { "1m": 1, "3m": 3, "6m": 6, "12m": 12 }[filters.period] || 0;
      if (monthsBack > 0) {
        const cutoff = new Date(
          now.getFullYear(),
          now.getMonth() - monthsBack,
          1,
        );
        filtered = filtered.filter(
          (c) => c.createdAt && new Date(c.createdAt) >= cutoff,
        );
      }
    }

    // Status filter
    if (filters.status !== "all") {
      if (filters.status === "overdue") {
        filtered = filtered.filter(
          (c) => billingData?.[c.cnpj]?.financial?.status === "overdue",
        );
      } else {
        filtered = filtered.filter(
          (c) => (c.status || "active") === filters.status,
        );
      }
    }

    // Plan filter
    if (filters.plan !== "all") {
      filtered = filtered.filter((c) => c.planType === filters.plan);
    }

    return filtered;
  }, [allCompanies, filters, billingData]);

  // Trend data for chart (always use all companies, not filtered)
  const trendData = useMemo(
    () => generateTrendData(allCompanies, billingData),
    [allCompanies, billingData],
  );

  const fetchBillingData = async (companiesList) => {
    const cnpjs = companiesList.map((c) => c.cnpj).filter(Boolean);

    if (cnpjs.length === 0) return;

    try {
      const nextBillingData = {};

      for (let index = 0; index < cnpjs.length; index += BILLING_BATCH_SIZE) {
        const batch = cnpjs.slice(index, index + BILLING_BATCH_SIZE);
        const res = await apiFetch(
          `/api/internal/billing/batch?cnpjs=${batch.join(",")}`,
        );

        if (!res.ok) {
          throw new Error(
            `Billing batch request failed with status ${res.status}`,
          );
        }

        const data = await res.json();
        Object.assign(nextBillingData, data);
      }

      setBillingData(nextBillingData);
    } catch (error) {
      console.error("Failed to fetch billing data", error);
    }
  };

  // Quando companies chegam do layout, buscar billing e atualizar loading
  useEffect(() => {
    if (allCompanies.length > 0) {
      setIsLoading(false);
      fetchBillingData(allCompanies);
    }
  }, [allCompanies]);

  // Fetch all plans (including non-public) for admin dashboard
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiFetch("/api/plans/admin/all");
        if (res.ok) {
          const data = await res.json();
          setAvailablePlans(data.map((p) => ({ id: p.slug, name: p.name })));
        }
      } catch (error) {
        console.error("Failed to fetch plans", error);
      }
    };
    fetchPlans();
  }, []);

  const defaultPlanType = availablePlans[0]?.id || "";

  const openCreateModal = () => {
    setEditingCompany(null);
    setFormData({
      companyName: "",
      cnpj: "",
      planType: defaultPlanType,
      status: "active",
      billingCycle: "monthly",
      commercialDiscountType: "percent",
      commercialDiscountValue: "",
      commercialDiscountExpiresAt: "",
      commercialDiscountDescription: "",
    });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      companyName: company.companyName,
      cnpj: formatCNPJ(company.cnpj),
      planType: company.planType,
      status: company.status || "active",
      billingCycle: company.billingCycle || "monthly",
      commercialDiscountType: company.commercialDiscountType || "percent",
      commercialDiscountValue: company.commercialDiscountValue ?? "",
      commercialDiscountExpiresAt: company.commercialDiscountExpiresAt
        ? new Date(company.commercialDiscountExpiresAt)
            .toISOString()
            .slice(0, 10)
        : "",
      commercialDiscountDescription:
        company.commercialDiscountDescription || "",
      updateAsaas: false,
    });
    setFormError("");
    setShowModal(true);
  };

  // Valida o desconto comercial contra o valor mensal do plano (ajustado pelo ciclo).
  // Retorna mensagem de erro ou null. Espelha a validação do backend.
  const getDiscountValidationError = () => {
    if (!editingCompany) return null;
    const { commercialDiscountType: type, commercialDiscountValue: raw } =
      formData;
    if (raw === "" || raw == null) return null;
    const value = Number(raw);
    if (Number.isNaN(value)) return null;
    if (type === "percent") {
      return value < 0 || value > 100
        ? "Percentual deve ser entre 0 e 100."
        : null;
    }
    // fixo: não pode exceder o valor mensal do plano
    const monthly = Number(editingCompany.plan?.monthlyPrice || 0);
    const yearly = editingCompany.plan?.yearlyPrice
      ? Number(editingCompany.plan.yearlyPrice) / 12
      : null;
    const planMonthly =
      formData.billingCycle === "anual" && yearly != null ? yearly : monthly;
    return value > planMonthly
      ? `Desconto não pode ser maior que o valor do plano (R$ ${planMonthly.toFixed(2).replace(".", ",")}).`
      : null;
  };
  const discountValidationError = getDiscountValidationError();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (discountValidationError) {
      setFormError(discountValidationError);
      return;
    }
    setIsSubmitting(true);
    setFormError("");

    try {
      const isEditing = !!editingCompany;

      const res = await apiFetch(
        isEditing
          ? `/api/internal/companies/${editingCompany.id}`
          : "/api/internal/companies",
        {
          method: isEditing ? "PUT" : "POST",
          body: JSON.stringify(formData),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Erro ao salvar empresa.");
      }

      // Success - refresh list and close modal
      setShowModal(false);
      setFormData({
        companyName: "",
        cnpj: "",
        planType: defaultPlanType,
        status: "active",
      });
      setEditingCompany(null);
      if (refreshCompanies) refreshCompanies();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCNPJ = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .substring(0, 18);
  };

  // ========================================
  // SaaS Metrics Computation
  // ========================================
  const metrics = useMemo(() => {
    if (!companies.length) return null;

    const now = new Date();
    const thisMonth = now.toISOString().slice(0, 7); // YYYY-MM
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      .toISOString()
      .slice(0, 7);

    let mrr = 0;
    let activeCount = 0;
    let churnedCount = 0;
    let trialCount = 0;
    let trialExpiredCount = 0;
    let overdueCount = 0;
    let newThisMonth = 0;
    let newLastMonth = 0;
    let totalLifespanMonths = 0; // soma dos meses de vida de todos os clientes
    let clientsWithLifespan = 0;
    let clientCount = 0; // Clientes que já pagaram (confirmedCount > 0)

    for (const c of companies) {
      const isActive =
        c.status === "active" || !c.status || c.status === "trial";
      // Churn = conta REALMENTE cancelada. 'suspended' é inadimplência temporária
      // (quem já pagava e deixou de pagar) — não é churn; 'trial_expired' é trial
      // não convertido — também não é churn.
      const isRealChurn = c.status === "churned";
      const createdMonth = c.createdAt?.slice(0, 7);

      // Conta clientes do mês
      if (createdMonth === thisMonth) newThisMonth++;
      if (createdMonth === lastMonth) newLastMonth++;

      // Tempo de vida do cliente (meses desde criação até agora ou até o cancelamento)
      if (c.createdAt) {
        const created = new Date(c.createdAt);
        const endDate = isRealChurn && c.updatedAt ? new Date(c.updatedAt) : now;
        const months = Math.max(
          1,
          (endDate - created) / (1000 * 60 * 60 * 24 * 30),
        );
        totalLifespanMonths += months;
        clientsWithLifespan++;
      }

      // Billing data from Asaas
      const billing = billingData?.[c.cnpj];
      const confirmedCount = billing?.financial?.confirmedCount || 0;
      const isClient = confirmedCount > 0; // Já efetuou pagamento = cliente

      // Status de trial vs cliente
      if (c.trialInfo) {
        if (c.trialInfo.isExpired) {
          trialExpiredCount++;
        } else if (!isClient) {
          trialCount++;
        }
      }

      if (isClient) clientCount++;

      // Churn = conta realmente cancelada (churned) que JÁ FOI cliente pagante.
      // Trial não convertido (nunca pagou) e inadimplência (suspended) não contam.
      if (isRealChurn && isClient) churnedCount++;

      // MRR: Asaas é a fonte de verdade. Inclui apenas clientes que já pagaram.
      const subValue = billing?.subscriptions?.[0]?.value;
      const planPrice = c.plan?.monthlyPrice;
      const clientMrr = subValue || planPrice || 0;

      if (isActive && isClient) {
        mrr += Number(clientMrr);
        activeCount++;
      }

      // Financeiro com problema
      if (billing?.financial?.status === "overdue") {
        overdueCount++;
      }
    }

    const arpu = activeCount > 0 ? mrr / activeCount : 0;
    const arr = mrr * 12;
    const growthRate =
      newLastMonth > 0
        ? ((newThisMonth - newLastMonth) / newLastMonth) * 100
        : newThisMonth > 0
          ? 100
          : 0;

    // Churn Rate = churned / total de clientes que já existiam (mensal é aproximação)
    // Usando total de clientes como base
    const totalEverActive = activeCount + churnedCount;
    const churnRate =
      totalEverActive > 0 ? (churnedCount / totalEverActive) * 100 : 0;

    // Tempo médio de vida (meses)
    const avgLifespanMonths =
      clientsWithLifespan > 0 ? totalLifespanMonths / clientsWithLifespan : 0;

    // LTV = ARPU × tempo médio de vida em meses
    // Se não há churn, usa o tempo médio atual como estimativa conservadora
    const ltv =
      churnRate > 0
        ? arpu / (churnRate / 100) // LTV clássico: ARPU / churn rate mensal
        : arpu * Math.max(avgLifespanMonths, 12); // Sem churn: estima 12 meses mínimo

    return {
      mrr,
      arr,
      activeCount,
      clientCount,
      totalCount: companies.length,
      arpu,
      trialCount,
      trialExpiredCount,
      overdueCount,
      churnedCount,
      churnRate,
      ltv,
      avgLifespanMonths,
      newThisMonth,
      newLastMonth,
      growthRate,
    };
  }, [companies, billingData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground-strong tracking-tight">
            Dashboard
          </h2>
          <p className="text-foreground-muted mt-1">
            Visão geral do negócio — métricas SaaS
          </p>
        </div>

        <div className="flex items-center gap-3">
          <DashboardFilters
            filters={filters}
            onChange={setFilters}
            plans={availablePlans}
          />
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2.5 rounded-xl font-semibold transition-all shadow-lg shadow-cyan-900/20"
          >
            <PlusCircle className="w-5 h-5" />
            Nova Empresa
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="space-y-3">
          {/* Row 1: Revenue metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* MRR */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <DollarSign className="w-3.5 h-3.5" />
                MRR
              </div>
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                {formatCurrency(metrics.mrr)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                ARR {formatCurrency(metrics.arr)} · {metrics.clientCount}{" "}
                pagante{metrics.clientCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* ARPU */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <BarChart3 className="w-3.5 h-3.5" />
                ARPU
              </div>
              <div className="text-xl font-bold text-cyan-700 dark:text-cyan-400">
                {formatCurrency(metrics.arpu)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                receita média/cliente
              </div>
            </div>

            {/* LTV */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Heart className="w-3.5 h-3.5" />
                LTV
              </div>
              <div className="text-xl font-bold text-violet-700 dark:text-violet-400">
                {formatCurrency(metrics.ltv)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.churnRate > 0
                  ? `ARPU ÷ ${metrics.churnRate.toFixed(1)}% churn`
                  : `~${Math.round(metrics.avgLifespanMonths)}m vida média`}
              </div>
            </div>

            {/* LTV:CAC placeholder */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Infinity className="w-3.5 h-3.5" />
                LTV:CAC
              </div>
              <div className="text-xl font-bold text-foreground-muted">—</div>
              <div className="text-[11px] text-foreground-muted mt-1">
                configurar CAC
              </div>
            </div>
          </div>

          {/* Row 2: Growth & Health metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Clientes Ativos */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Users className="w-3.5 h-3.5" />
                Clientes
              </div>
              <div className="text-xl font-bold text-foreground-strong">
                {metrics.clientCount}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.trialCount > 0
                  ? `${metrics.trialCount} em trial · `
                  : ""}
                {metrics.totalCount} total
              </div>
            </div>

            {/* Growth */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Novos/Mês
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold text-foreground-strong">
                  {metrics.newThisMonth}
                </span>
                {metrics.growthRate !== 0 && (
                  <span
                    className={`flex items-center text-xs font-medium ${metrics.growthRate > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}
                  >
                    {metrics.growthRate > 0 ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3" />
                    )}
                    {Math.abs(metrics.growthRate).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                vs {metrics.newLastMonth} mês anterior
              </div>
            </div>

            {/* Churn */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <UserMinus className="w-3.5 h-3.5" />
                Churn
              </div>
              <div
                className={`text-xl font-bold ${metrics.churnRate > 5 ? "text-red-700 dark:text-red-400" : metrics.churnRate > 0 ? "text-yellow-700 dark:text-yellow-400" : "text-emerald-700 dark:text-emerald-400"}`}
              >
                {metrics.churnRate.toFixed(1)}%
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.churnedCount} cancelado
                {metrics.churnedCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Inadimplência */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Percent className="w-3.5 h-3.5" />
                Inadimplentes
              </div>
              <div
                className={`text-xl font-bold ${metrics.overdueCount > 0 ? "text-red-700 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}
              >
                {metrics.overdueCount}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.activeCount > 0
                  ? (
                      (metrics.overdueCount / metrics.activeCount) *
                      100
                    ).toFixed(1)
                  : 0}
                % da base
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <TrendChart companies={allCompanies} billingData={billingData} />

      <CompanyTable
        data={companies}
        isLoading={isLoading}
        onEdit={openEditModal}
        billingData={billingData}
      />

      {/* Billing Audit */}
      <BillingAuditPanel />

      {/* AI CFO Assistant */}
      <CFOAssistant
        metrics={metrics}
        companies={allCompanies}
        billingData={billingData}
        trendData={trendData}
      />

      {/* Modal for adding/editing company */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-surface border border-border-strong rounded-2xl p-6 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground-strong">
                {editingCompany ? "Editar Empresa" : "Nova Empresa"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-foreground-muted hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400 text-sm">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  Nome da Empresa
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-foreground-muted" />
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) =>
                      setFormData({ ...formData, companyName: e.target.value })
                    }
                    placeholder="Ex: Empresa ABC Ltda"
                    className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  CNPJ
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3.5 w-5 h-5 text-foreground-muted" />
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cnpj: formatCNPJ(e.target.value),
                      })
                    }
                    placeholder="00.000.000/0000-00"
                    className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">
                  Tipo de Plano
                </label>
                <div className="relative">
                  <div className="">
                    <Select
                      value={formData.planType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, planType: value })
                      }
                    >
                      <SelectTrigger className="w-full bg-background border-border text-foreground-strong">
                        <SelectValue placeholder="Selecione o plano" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePlans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Status (only in edit mode) */}
              {editingCompany && (
                <div>
                  <label className="block text-sm font-medium text-foreground-secondary mb-2">
                    Status da Conta
                  </label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTrigger className="w-full bg-background border-border text-foreground-strong">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-400" />
                          Ativo
                        </span>
                      </SelectItem>
                      <SelectItem value="trial">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-400" />
                          Trial
                        </span>
                      </SelectItem>
                      <SelectItem value="suspended">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-orange-400" />
                          Suspenso
                        </span>
                      </SelectItem>
                      <SelectItem value="churned">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-400" />
                          Cancelado
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Billing cycle (edit only) */}
              {editingCompany && (
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-foreground-secondary mb-2">
                    <CalendarDays className="w-4 h-4" />
                    Ciclo de cobrança
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: "monthly", label: "Mensal" },
                      { value: "anual", label: "Anual" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, billingCycle: opt.value })
                        }
                        className={`flex-1 py-2 text-sm rounded-xl border transition-colors ${
                          formData.billingCycle === opt.value
                            ? "border-cyan-500 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300"
                            : "border-border text-foreground-muted hover:border-border-strong"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Commercial discount (edit only) */}
              {editingCompany && (
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-foreground-secondary mb-2">
                    <Tag className="w-4 h-4" />
                    Desconto comercial (opcional)
                  </label>

                  {/* Discount type toggle */}
                  <div className="flex gap-2 mb-2">
                    {[
                      { value: "percent", label: "%" },
                      { value: "fixed", label: "R$" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            commercialDiscountType: opt.value,
                            commercialDiscountValue: "",
                          })
                        }
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          formData.commercialDiscountType === opt.value
                            ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                            : "border-border text-foreground-muted hover:border-border-strong"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-foreground-muted mb-1 block">
                        {formData.commercialDiscountType === "percent"
                          ? "Desconto (%)"
                          : "Desconto (R$)"}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={
                          formData.commercialDiscountType === "percent"
                            ? 100
                            : undefined
                        }
                        step="0.01"
                        value={formData.commercialDiscountValue}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            commercialDiscountValue: e.target.value,
                          })
                        }
                        placeholder={
                          formData.commercialDiscountType === "percent"
                            ? "Ex.: 15"
                            : "Ex.: 100"
                        }
                        className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-all"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-foreground-muted mb-1 flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        Válido até (opcional)
                      </label>
                      <input
                        type="date"
                        value={formData.commercialDiscountExpiresAt}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            commercialDiscountExpiresAt: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong focus:outline-none focus:border-cyan-500 transition-all"
                        title="Válido até (opcional)"
                      />
                    </div>
                  </div>

                  {/* Discount description */}
                  <div className="mt-2">
                    <label className="text-xs text-foreground-muted mb-1 block">
                      Descrição do desconto (opcional)
                    </label>
                    <input
                      type="text"
                      maxLength={120}
                      value={formData.commercialDiscountDescription}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          commercialDiscountDescription: e.target.value,
                        })
                      }
                      placeholder="Ex.: Parceria comercial 2026"
                      className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground-strong placeholder:text-placeholder focus:outline-none focus:border-cyan-500 transition-all"
                    />
                  </div>

                  {formData.commercialDiscountValue > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                      Desconto de{" "}
                      {formData.commercialDiscountType === "percent"
                        ? `${formData.commercialDiscountValue}%`
                        : `R$ ${Number(formData.commercialDiscountValue).toFixed(2).replace(".", ",")}`}
                      {formData.commercialDiscountExpiresAt &&
                        ` até ${new Date(formData.commercialDiscountExpiresAt).toLocaleDateString("pt-BR")}`}
                    </p>
                  )}
                </div>
              )}

              {/* Discount validation error */}
              {editingCompany && discountValidationError && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {discountValidationError}
                </p>
              )}

              {/* Update Asaas checkbox (edit only) */}
              {editingCompany && (
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.updateAsaas}
                    onChange={(e) =>
                      setFormData({ ...formData, updateAsaas: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 rounded border-border text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-foreground-secondary">
                    Atualizar valor no Asaas
                    <span className="block text-xs text-foreground-muted">
                      Recalcula plano + addons − descontos e atualiza a assinatura
                      mensal no Asaas.
                    </span>
                  </span>
                </label>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-surface-active hover:bg-border-strong text-foreground-secondary font-medium rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !!discountValidationError}
                  className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : editingCompany ? (
                    "Salvar"
                  ) : (
                    "Criar Empresa"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
